import { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '@/utils';
import { API_ENDPOINTS } from '@/constants';
import { mapTestValuesToUUIDs, type FormulaTestValues } from './formulaUtils';

import type { CampaignQuestion } from '../CampaignQuestionsEditor/types';

interface UseFormulaCalculationOptions {
    formula: string;
    testValues: FormulaTestValues;
    questions: CampaignQuestion[];
    testMode: boolean;
    isValid: boolean;
}

interface UseFormulaCalculationResult {
    calculatedResult: number | null;
    isCalculating: boolean;
    error: boolean;
}

/**
 * Custom hook for calculating score formula results when testing formulas in the formula builder UI.
 * - Debounces requests to avoid excessive API calls during rapid input
 * - Automatically cancels in-flight requests when inputs change
 *
 * @param {UseFormulaCalculationOptions} options
 * @returns {UseFormulaCalculationResult}
 */
export const useFormulaCalculation = (options: UseFormulaCalculationOptions): UseFormulaCalculationResult => {
    const { formula, testValues, questions, testMode, isValid } = options;

    const [state, setState] = useState<UseFormulaCalculationResult>({ calculatedResult: null, isCalculating: false, error: false });
    const abortControllerRef = useRef<AbortController | null>(null);

    const shouldCalculate = testMode && isValid; // Only calculate if test mode is enabled and formula is valid
    const uuidBasedValues = useMemo(() => mapTestValuesToUUIDs(testValues, questions), [testValues, questions]);

    useEffect(() => {
        if (!shouldCalculate) {
            setState({ calculatedResult: null, isCalculating: false, error: false });
            return;
        }

        abortControllerRef.current?.abort();

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const timeoutId = setTimeout(async () => {
            setState(prev => ({ ...prev, isCalculating: true, error: false }));

            try {
                const endpoint = API_ENDPOINTS.CAMPAIGN_QUESTIONS_CALCULATE_FORMULA;
                const body = { formula, values: uuidBasedValues, questions };
                const requestConfig = { signal: abortController.signal };

                const response = await api.post(endpoint, body, requestConfig);

                if (!abortController.signal.aborted) {
                    setState({ calculatedResult: response.data ?? 0, isCalculating: false, error: false });
                }
            } catch (error) {
                if (error instanceof Error && error.name === 'CanceledError') {
                    return;
                }

                setState({ calculatedResult: null, isCalculating: false, error: true });
            }
        }, 400); // 400ms debounce

        return () => {
            clearTimeout(timeoutId);
            abortController.abort();
        };
    }, [formula, uuidBasedValues, questions, shouldCalculate]);

    return state;
};
